import React from "react";
import { useNavigate } from 'react-router-dom';
import { Col, Row, Statistic, Spin, Modal, List, Button, Tooltip, Skeleton, Typography, Table, Badge, Input, Space } from 'antd';
import { NodeIndexOutlined, WifiOutlined, EyeOutlined, ApartmentOutlined, PieChartOutlined } from '@ant-design/icons';

import useGetData from "../../hooks/useGetData";
import { GetSummaryDashBoard, getDashBoardData } from '../../api/dashboard';
import { getAllSites } from '../../api/site';
import { getDeviceDataWithFilters } from '../../api/device';
import { humanFileSize } from '../../services/utils';

import DashboardCard from "../../components/DashboardCard";

import { SearchOutlined } from '@ant-design/icons';

import type { ColumnType } from 'antd/es/table';
import type { ColumnGroupType, FilterConfirmProps } from 'antd/es/table/interface';
import type { DataType } from '../../api/device';
import type { InputRef } from 'antd';
import Highlighter from "react-highlight-words";

type DataIndex = keyof DataType;

const DashboardPage: React.FC = () => {
    const navigate = useNavigate();    
    
    const { data, isLoading, isFetching, isError, error } = useGetData('dashboard', getDashBoardData);
    const [dataDashBoard, setDataDashBoard] = React.useState<GetSummaryDashBoard | undefined>();

    const [listSitesModalOpen, setListSitesModalOpen] = React.useState(false);
    const [sites, setSites] = React.useState();

    const [searchText, setSearchText] = React.useState('');
    const [searchedColumn, setSearchedColumn] = React.useState('');
    const searchInput = React.useRef<InputRef>(null);

    const [openOfflineModal, setOpenOfflineModal] = React.useState(false);

    const [dataOfflineDevice, setDataOfflineDevice] = React.useState<DataType[] | any>();
    const [offlineDeviceFetching, setOfflineDeviceFetching] = React.useState(false);

    React.useEffect(() => {
        if (isError) {
            console.error('dashboard_error', error);
        }
        if ((!isLoading || !isFetching) && !isError && data) {
            setDataDashBoard(data);
        }
    }, [isLoading, isFetching, isError, data, error]);

    const handleSitesCardClick = React.useCallback(async () => {
        setListSitesModalOpen(true);

        let sites;
        let isExpired = false;
        let hasLocal = false;
        const dataFromLocal = localStorage.getItem("LIST_SITES");
        const now = new Date();
        if (dataFromLocal && dataFromLocal !== "") {
            hasLocal = true;
            const sitesObj = JSON.parse(dataFromLocal);
            if (now.getTime() > sitesObj.expire) {
                isExpired = true;
            }
        }

        let canRequest = false;
        if ((hasLocal && isExpired) || !hasLocal) {
            canRequest = true;
        }

        if (canRequest) {
            let res = await getAllSites();
            if (res) {
                sites = res;
                let siteJson = JSON.stringify({
                    data: res,
                    expired: now.getTime() + (5 * 60 * 1000)
                });
                localStorage.setItem('LIST_SITES', siteJson)
            }
        }
        else if (!canRequest && dataFromLocal) {
            sites = JSON.parse(dataFromLocal)['data'];
        }

        setSites(sites);

    }, []);

    const handleFetchOfflineDevice = React.useCallback(async () => {
        setOpenOfflineModal(true);
        setOfflineDeviceFetching(true);

        const devices = await getDeviceDataWithFilters("status=down");
        // console.log(devices)

        setOfflineDeviceFetching(false);
        setDataOfflineDevice(devices);

    }, []);

    const getColumnSearchProps = React.useCallback((dataIndex: DataIndex): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },

        render: (text) =>
            searchedColumn !== dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    }), []);

    const columns: (ColumnType<DataType> | ColumnGroupType<DataType>)[] = React.useMemo(() => [
        {
            title: 'Site Name',
            dataIndex: 'site_name',
            key: 'site_name',
            ...getColumnSearchProps('site_name'),
            sorter: (a, b) => a.site_name.length - b.site_name.length,
            sortDirections: ['descend', 'ascend'],
        },
    
        {
            title: 'Device Name',
            dataIndex: 'name',
            key: 'name',
            ...getColumnSearchProps('name'),
            sorter: (a, b) => a.name.length - b.name.length,
            sortDirections: ['descend', 'ascend'],
        },
    
        {
            title: 'State',
            dataIndex: 'status',
            key: 'status',
            ...getColumnSearchProps('status'),
            sorter: (a, b) => a.status.length - b.status.length,
            sortDirections: ['descend', 'ascend'],
            render: (text) => <Badge color={text === 'up' ? 'green' : 'red'} text={text} />
        },

        {
            title: 'Down duration',
            dataIndex: 'numberOfSecondsSinceLastCommunication',
            key: 'numberOfSecondsSinceLastCommunication',
            ...getColumnSearchProps('numberOfSecondsSinceLastCommunication'),
            sorter: (a, b) => a.numberOfSecondsSinceLastCommunication - b.numberOfSecondsSinceLastCommunication,
            sortDirections: ['descend', 'ascend'],
            render: (number) => `${( parseInt(number) / 86400).toFixed(0)} days ago`,            
        }
    ], []) 
    

    const handleSearch = React.useCallback((
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);

    }, []);

    const handleReset = React.useCallback((clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    }, []);

    return (
        <>
            <Spin size="large" spinning={isLoading || isFetching}>                
                <div className="site-statistic-demo-card">
                    <Row gutter={24}>
                        <Col span={12}>
                            <DashboardCard
                                title="Sites"
                                contents={dataDashBoard ? dataDashBoard.totalSites : 0}
                                description="Total sites"
                                hoverable
                                actions={[<Statistic value={"View all sites"} prefix={<EyeOutlined />} />]}
                                onCardClick={handleSitesCardClick}
                            />
                        </Col>
                        <Col span={12}>
                            <DashboardCard
                                title="Inventory"
                                contents={dataDashBoard ? `${dataDashBoard.deviceUpCount} / ${dataDashBoard.deviceCount}` : "0 / 0"}
                                description="Online devices"
                                hoverable
                                actions={[
                                    (
                                        <Statistic 
                                            value={"Go to devices page"} 
                                            prefix={<ApartmentOutlined />} 
                                            valueStyle={{ fontSize: 20 }}
                                        />
                                    ),
                                    (
                                        <Button type="primary" onClick={handleFetchOfflineDevice}>
                                            <Typography.Text id="off-btn" style={{ color: '#FFFFFF', fontWeight: 'bold' }}>View Offline Device</Typography.Text>
                                        </Button>
                                    )
                                ]}
                                onCardClick={(e) => {
                                    console.log(e)
                                    console.log(e.currentTarget)
                                    console.log(e.target)
                                    
                                    if ((e.target as EventTarget & HTMLDivElement).id !== "off-btn") {
                                        navigate("/devices")
                                    }                                        
                                }}
                            />
                        </Col>
                    </Row>

                    <Row gutter={24} style={{ marginTop: 10, justifyContent: 'center' }}>
                        {/* <Col span={12}>
                            <Card hoverable>
                                <Statistic
                                    title="Total Client (Wire)"
                                    value={dataDashBoard ? dataDashBoard.wiredClientsCount : 0}
                                    valueStyle={{ color: 'black' }}
                                />
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card hoverable>
                                <Statistic
                                    title="Total Client (Wireless)"
                                    value={dataDashBoard ? dataDashBoard.wirelessClientsCount : 0}
                                    valueStyle={{ color: 'black' }}
                                />
                            </Card>
                        </Col> */}
                        <Col span={12}>
                            <DashboardCard
                                title="Clients"
                                contents={dataDashBoard ? dataDashBoard.wiredClientsCount + dataDashBoard.wirelessClientsCount : 0}
                                description="Active clients"
                                hoverable
                                actions={[
                                    (
                                        <Tooltip title="Wired connection" color="#FF8300" placement="bottom">
                                            <Statistic value={dataDashBoard ? dataDashBoard.wiredClientsCount : 0} prefix={<NodeIndexOutlined />} />
                                        </Tooltip>
                                    ),
                                    (
                                        <Tooltip title="Wireless connection" color="#FF8300" placement="bottom">
                                            <Statistic value={dataDashBoard ? dataDashBoard.wirelessClientsCount : 0} prefix={<WifiOutlined />} />
                                        </Tooltip>
                                    )
                                ]}
                                onCardClick={() => navigate("/clients")}
                            />
                        </Col>
                        <Col span={12}>
                            <DashboardCard
                                title="Applications"
                                contents={dataDashBoard ? humanFileSize(dataDashBoard.totalDataTransferredDuringLast24HoursInBytes) : `0 B`}
                                description="Transferred (last 24hrs)"
                                hoverable
                                actions={[<Statistic value={"Go to applications page"} prefix={<PieChartOutlined />} />]}
                                onCardClick={() => navigate("/applications")}
                            />
                        </Col>
                    </Row>
                </div>
                <Modal
                    title="List of Sites"
                    centered
                    open={listSitesModalOpen}
                    onCancel={() => setListSitesModalOpen(false)}
                    onOk={() => setListSitesModalOpen(false)}
                    footer={[
                        <Button key={'s-ok-btn'} type="primary" onClick={() => setListSitesModalOpen(false)}>OK</Button>
                    ]}
                >
                    <div style={{
                        overflowY: 'scroll',
                        maxHeight: '600px',
                        minHeight: '300px'
                    }}>
                        <List
                            size="small"
                            itemLayout="horizontal"
                            dataSource={sites}
                            renderItem={(item: any) => (
                                <List.Item>
                                    <Skeleton loading={sites === null || sites === undefined} active>
                                        <List.Item.Meta
                                            title={item.name}
                                            description={item.id}
                                        />
                                    </Skeleton>
                                </List.Item>
                            )}
                        />
                    </div>
                </Modal>
                <Modal
                    title="Offline Devices"
                    centered
                    open={openOfflineModal}
                    onCancel={() => setOpenOfflineModal(false)}
                    onOk={() => setOpenOfflineModal(false)}
                    footer={[
                        <Button key={'off-ok-btn'} type="primary" onClick={() => setOpenOfflineModal(false)}>OK</Button>
                    ]}
                >
                    <Table 
                        columns={columns}
                        loading={offlineDeviceFetching}
                        dataSource={dataOfflineDevice}
                        size="small"
                        rowKey="name"
                    />
                </Modal>
            </Spin>
        </>
    )
}

export default DashboardPage;